const WEB_PLAYBACK_STORAGE_KEY = 'cmf.spotify.webPlayback.override';
const LOCAL_TRACK_LOOKUP_STORAGE_KEY = 'cmf.spotify.localSavedTrackLookup.override';
const MULTI_TAB_PLAYER_STORAGE_KEY = 'cmf.spotify.multiTabPlayer.override';

function readBoolean(value: string | null): boolean | null {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

function getEnvBoolean(name: string): boolean | null {
  const env = process.env as Record<string, string | undefined>;
  const raw = env[name];
  if (raw === undefined) return null;
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return null;
}

function readOverride(storageKey: string): boolean | null {
  if (typeof window !== 'undefined') {
    try {
      const overrideValue = window.localStorage?.getItem(storageKey) ?? null;
      const parsedOverride = readBoolean(overrideValue);
      if (parsedOverride !== null) {
        return parsedOverride;
      }
    } catch {
      // Ignore storage failures; fall back to defaults
    // Ignore storage failures; fall back to env/defaults
    }
  }
  return null;
}

function writeOverride(storageKey: string, value: boolean | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage?.removeItem(storageKey);
    } else {
      window.localStorage?.setItem(storageKey, value ? 'true' : 'false');
    }
  } catch {
    // Ignore storage failures
  }
}

function resolveFlag({
  storageKey,
  envVarName,
  fallback
}: {
  storageKey: string;
  envVarName: string;
  fallback: boolean;
}): boolean {
  const override = readOverride(storageKey);
  if (override !== null) return override;

  const envValue = getEnvBoolean(envVarName);
  if (envValue !== null) {
    return envValue;
  }

  return fallback;
}

export function isSpotifyWebPlaybackEnabled(): boolean {
  return resolveFlag({
    storageKey: WEB_PLAYBACK_STORAGE_KEY,
    envVarName: 'REACT_APP_ENABLE_SPOTIFY_WEB_PLAYBACK',
    fallback: true
  });
}

export function setSpotifyWebPlaybackOverride(value: boolean | null): void {
  writeOverride(WEB_PLAYBACK_STORAGE_KEY, value);
}

export function isLocalSavedTrackLookupEnabled(): boolean {
  return resolveFlag({
    storageKey: LOCAL_TRACK_LOOKUP_STORAGE_KEY,
    envVarName: 'REACT_APP_USE_LOCAL_SAVED_TRACK_LOOKUPS',
    fallback: false
  });
}

export function setLocalSavedTrackLookupOverride(value: boolean | null): void {
  writeOverride(LOCAL_TRACK_LOOKUP_STORAGE_KEY, value);
}

export function isMultiTabPlayerEnabled(): boolean {
  const override = readOverride(MULTI_TAB_PLAYER_STORAGE_KEY);
  if (override !== null) return override;

  const envValue = getEnvBoolean('NEXT_PUBLIC_SPOTIFY_MULTI_TAB_PLAYER');
  if (envValue !== null) {
    return envValue;
  }

  // Default ON for rollout; override via env or local storage as needed.
  return true;
}

export function setMultiTabPlayerOverride(value: boolean | null): void {
  writeOverride(MULTI_TAB_PLAYER_STORAGE_KEY, value);
}
